import React, { useEffect, useState, useCallback } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import Loader from "../common/Loader.jsx";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import { getAllOutstandingReportSchedulesApi } from "./accounts-api";
import { sanitizeInput } from "../common/security-utils.js";
import Back from "../../assets/svg/back.svg";
import Search from "../../assets/svg/search.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Renders the same schedule summary sentence as OutstandingReportModal's
// Schedule tab, so the wording is consistent wherever a schedule is shown.
const summarizeSchedule = (schedule) => {
  const interval = schedule.interval || 1;
  const time = schedule.send_time ? schedule.send_time.slice(0, 5) : "";

  if (schedule.frequency === "daily") {
    const suffix = schedule.weekdays_only ? " (week days only)" : "";
    return `Every ${interval} day(s)${suffix} at ${time}`;
  }
  if (schedule.frequency === "weekly") {
    const dayNames = (schedule.weekdays || []).map((d) => WEEKDAY_NAMES[d]).join(", ") || "no days selected";
    return `Every ${interval} week(s) on ${dayNames} at ${time}`;
  }
  if (schedule.monthly_mode === "on_day") {
    return `Every ${interval} month(s) on day ${schedule.monthly_day} at ${time}`;
  }
  const weekday = (schedule.weekdays || [])[0];
  return `Every ${interval} month(s) on the ${schedule.monthly_week_ordinal || ""} ${
    weekday != null ? WEEKDAY_NAMES[weekday] : ""
  } at ${time}`;
};

const OutstandingSchedulesList = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = `?search=${encodeURIComponent(search)}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}`;
      const response = await getAllOutstandingReportSchedulesApi(params);
      if (response?.data?.success) {
        setSchedules(response.data.schedules || []);
        setRecords(response.data.total_rec || 0);
        setPageCount(response.data.total_pgs || 0);
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [search, itemsPerPage, itemOffset]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handlePageClick = (event) => {
    setItemOffset(event.selected);
  };

  return (
    <div className="account-page">
      <div className="d-flex align-items-center gap-3 mb-3">
        <img
          src={Back}
          alt="back"
          className="back-icon"
          style={{ cursor: "pointer", width: "24px", height: "24px" }}
          onClick={() => navigate(-1)}
        />
        <h4 className="mb-0" style={{ fontWeight: 700 }}>Outstanding Report Schedules</h4>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "100px",
          border: "0.8px solid #e5e5e5",
          padding: "8px 14px",
          backgroundColor: "#ffffff",
          height: "36px",
          width: "320px",
          marginBottom: "16px",
        }}
      >
        <img src={Search} alt="search" style={{ width: "14px" }} />
        <input
          type="search"
          placeholder="Search by customer or email..."
          value={search}
          onChange={(e) => {
            setSearch(sanitizeInput(e.target.value));
            setItemOffset(0);
          }}
          className="no-focus-border"
          style={{ border: "none", outline: "none", fontSize: "13px", background: "transparent", flex: 1 }}
        />
      </div>

      <div className="view-card">
        <div className="view-card-title">
          <span>Schedules - {records}</span>
        </div>
        <table className="custom-table">
          <thead>
            <tr>
              <th style={{ width: "40px" }}>S.No</th>
              <th>Customer</th>
              <th>Frequency</th>
              <th>Send To</th>
              <th>Start Date</th>
              <th>End Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                  <Loader height="120px" />
                </td>
              </tr>
            ) : schedules.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", color: "#8790A1" }}>
                  {search ? "No schedules match your search." : "No outstanding report schedules yet."}
                </td>
              </tr>
            ) : (
              schedules.map((schedule, index) => (
                <tr
                  key={schedule.id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate("/accounts/view", {
                      state: {
                        id: schedule.customer_id,
                        openScheduleModal: true,
                        initialModalTab: "Schedule",
                        scheduleId: schedule.id,
                      },
                    })
                  }
                >
                  <td>{itemOffset * itemsPerPage + index + 1}</td>
                  <td style={{ color: "#3D4AF5", fontWeight: "500" }}>{schedule.customer_name}</td>
                  <td style={{ textTransform: "capitalize" }}>{summarizeSchedule(schedule)}</td>
                  <td>{schedule.to_email || "-"}</td>
                  <td>{schedule.start_date || "-"}</td>
                  <td>{schedule.end_date || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {records > 0 && (
          <div className="d-flex align-items-center justify-content-between" style={{ padding: "12px 4px 0" }}>
            <RecordsPerPage
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setItemOffset(0);
              }}
            />
            <ReactPaginate
              key={itemOffset}
              previousLabel={<img src={lar} className="ar3" alt="previous" />}
              nextLabel={<img src={rar} className="ar3" alt="next" />}
              breakLabel={"..."}
              pageCount={pageCount + 1}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              forcePage={itemOffset}
              containerClassName={"pagination"}
              activeClassName={"active"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OutstandingSchedulesList;
